/**
 * 游戏卡片滑动距离缓存模块
 *
 * 功能：
 * - 动态测量游戏卡片的实际尺寸
 * - 缓存滑动距离以提高性能
 * - 支持缓存失效和重新计算
 * - 监听窗口大小变化自动清理缓存
 */

interface GameCardSize {
    /** 单个游戏卡片的宽度（包含margin） */
    singleWidth: number
    /** 滑动距离（通常是3个卡片的宽度） */
    slideDistance: number
    /** 缓存创建时间戳 */
    timestamp: number
}

class GameCardCache {
    private cache = new Map<string, GameCardSize>()
    private readonly gameCount = 3 // 每次滑动3个游戏
    private readonly expireTime = 10 * 60 * 1000 // 10分钟过期时间
    private isListening = false
    private resizeTimer: number | null = null

    constructor() {
        this.initializeListeners()
    }

    /**
     * 初始化事件监听器
     */
    private initializeListeners(): void {
        if (this.isListening) return

        // 监听窗口大小变化
        window.addEventListener('resize', () => this.handleResize())

        this.isListening = true
    }

    /**
     * 处理窗口大小变化（防抖处理）
     */
    private handleResize(): void {
        // 清除之前的定时器
        if (this.resizeTimer !== null) {
            clearTimeout(this.resizeTimer)
        }

        // 设置新的定时器，300ms后清理缓存
        this.resizeTimer = setTimeout(() => {
            this.clearCache()
            this.resizeTimer = null
        }, 300)
    }

    /**
     * 动态测量游戏卡片的实际尺寸
     */
    private measureGameItemSize(swiperInstance: any): number | null {
        if (!swiperInstance?.el) return null

        const gameItems = swiperInstance.el.querySelectorAll('.game-item')
        if (gameItems.length === 0) return null

        const firstItem = gameItems[0] as HTMLElement
        const rect = firstItem.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(firstItem)
        const marginRight = parseFloat(computedStyle.marginRight) || 0

        // 如果 getBoundingClientRect 返回 0，尝试其他方法
        let actualWidth = rect.width || firstItem.offsetWidth

        // 如果还是 0，尝试从 CSS 获取像素值
        if (actualWidth === 0 && computedStyle.width?.endsWith('px')) {
            actualWidth = parseFloat(computedStyle.width)
        }

        // 如果所有方法都失败，返回 null 使用备用值
        if (!actualWidth || actualWidth <= 0) {
            return null
        }

        return actualWidth + marginRight
    }

    /**
     * 备用的游戏卡片宽度（基于 CSS 定义）
     */
    private getFallbackWidth(type: string): number {
        // 使用 rem 单位计算，确保在不同设备上保持一致
        // 项目使用 rootValue: 75，即 1rem = 75px（设计稿基准）
        const remToPixel = (rem: number) => {
            const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
            return rem * rootFontSize
        }

        let fallbackValue
        if (type === 'history') {
            // history：CSS 定义 130px + 13px = 143px，143/75 = 1.91rem
            fallbackValue = remToPixel(1.91)
        } else {
            // 普通游戏：CSS 定义 225px + 14px = 239px，239/75 = 3.19rem
            fallbackValue = remToPixel(3.19)
        }

        return fallbackValue
    }

    /**
     * 检查缓存是否有效
     */
    private isCacheValid(cacheItem: GameCardSize): boolean {
        const now = Date.now()
        return now - cacheItem.timestamp < this.expireTime
    }

    /**
     * 获取游戏卡片的滑动距离
     * @param type 游戏类型
     * @param swiperInstance Swiper实例，用于DOM测量
     * @returns 滑动距离（像素）
     */
    getSlideDistance(type: string, swiperInstance: any): number {
        const cached = this.cache.get(type)
        if (cached && this.isCacheValid(cached)) {
            return cached.slideDistance
        }

        // 缓存不存在或已过期，重新测量
        const singleWidth = this.measureGameItemSize(swiperInstance)

        // 获取备用值
        const fallbackWidth = this.getFallbackWidth(type)

        if (singleWidth === null) {
            return fallbackWidth * this.gameCount
        }

        const slideDistance = singleWidth * this.gameCount

        // 更新缓存
        this.cache.set(type, {
            singleWidth,
            slideDistance,
            timestamp: Date.now(),
        })

        return slideDistance
    }

    /**
     * 预加载指定类型的滑动距离
     * @param type 游戏类型
     * @param swiperInstance Swiper实例
     */
    preloadSlideDistance(type: string, swiperInstance: any): void {
        if (!this.cache.has(type)) {
            this.getSlideDistance(type, swiperInstance)
        }
    }

    /**
     * 清理指定类型的缓存
     * @param type 游戏类型，不传则清理所有缓存
     */
    clearCache(type?: string): void {
        if (type) {
            this.cache.delete(type)
        } else {
            this.cache.clear()
        }
    }
}

const gameCardCache = new GameCardCache()

export { gameCardCache }
